<template>
  <Popper
    v-if="content"
    :content="content"
    :placement="placement"
    :hover="hover"
    :offset-distance="offsetDistance"
    :disabled="disabled"
    :show="show"
    arrow
  >
    <slot />

    <template #content>
      <!-- text-left: the tooltip renders inside the trigger's container, so it would
           otherwise inherit alignment from right-aligned table cells and rows. -->
      <div class="flex max-w-[400px] items-center truncate whitespace-normal py-1 px-2 text-left">
        <slot v-if="customContent && !title" v-bind="props" name="content" />

        <div v-else-if="title" class="flex gap-2 text-xs">
          <div :class="iconClass" class="align-text-top">
            <font-awesome-icon :icon="icon" size="lg" />
          </div>
          <div class="flex flex-col gap-2">
            <div class="text-md font-semibold">{{ title }}</div>
            <slot v-if="customContent" v-bind="props" name="content" />
            <div v-else>{{ content }}</div>
          </div>
        </div>

        <span v-else class="text-[12px] leading-[14px]">{{ content }}</span>
      </div>
    </template>
  </Popper>

  <slot v-else />
</template>

<script lang="ts" setup>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { onErrorCaptured } from 'vue';
import Popper from 'vue3-popper';

/*
Ways to use the component to create tooltips:

  - 1. Simple text (default):
        - Set 'content', but not 'title'.

  - 2. Icon + title + text
        - Set 'content', 'title', and optionally an 'icon' / 'iconClass'.

  - 3. Fancy content:
        - Set 'content', 'title', and optionally an 'icon' / 'iconClass'.
        - Set 'customContent' to true.
        - Use <template #content="{ content }"> cool stuff </template>.

Use 'show' to programmatically show/hide the tooltip.
 */
interface Props {
  title?: string;
  icon?: string[];
  iconClass?: string;
  content: string;
  customContent?: boolean;
  disabled?: boolean;
  hover?: boolean;
  offsetDistance?: string;
  show?: boolean;
  placement?:
    | 'auto'
    | 'auto-start'
    | 'auto-end'
    | 'top'
    | 'top-start'
    | 'top-end'
    | 'bottom'
    | 'bottom-start'
    | 'bottom-end'
    | 'right'
    | 'right-start'
    | 'right-end'
    | 'left'
    | 'left-start'
    | 'left-end';
}

const props = withDefaults(defineProps<Props>(), {
  title: undefined,
  icon: () => ['fal', 'circle-question'],
  iconClass: 'text-neutral-500',
  customContent: false,
  disabled: false,
  hover: true,
  show: undefined,
  offsetDistance: '0',
  placement: 'top'
});

onErrorCaptured((err) => {
  /*
    vue3-popper doesn't account for the fact that unmounting can happen without mounting first, leading to a crash.
    Unfortunately, vue3-popper looks stale and doesn't accept pull requests, so we catch and ignore these specific errors here.
   */
  if (err.stack?.includes('popper')) {
    console.warn(err.stack); // log it, but don't crash the app.
    return false;
  }
});
</script>

<style lang="css">
:root {
  --popper-theme-background-color: theme('colors.white');
  --popper-theme-background-color-hover: theme('colors.white');
  --popper-theme-text-color: theme('colors.neutral.700');
  --popper-theme-border-width: theme('borderWidth.2');
  --popper-theme-border-color: theme('colors.neutral.700');
  --popper-theme-border-radius: theme('borderRadius.lg');
  --popper-theme-padding: theme('padding.4');
  --popper-theme-box-shadow: theme('boxShadow.md');
}
</style>
